// Ação "Solicitar material" do tablet: só aparece para quem tem `estoque:solicitar`.
import type { ReactElement } from 'react';
import { rotuloCelula, type CelulaColetor } from '../../lib/coletor/celulas-coletor';

export interface PedidoMaterialTablet {
  readonly loteId?: string;
  readonly opId?: string;
  readonly celulaCodigo?: string;
  readonly referencia: string;
}

/**
 * Referência legível de um pedido por lote/OP: "Lote X · OP n". O número da OP pode chegar já prefixado
 * ("OP-42") de alguma fonte; o prefixo é removido para nunca mostrar "OP OP-42" ou "OP-OP-42".
 */
export function referenciaLoteOp(loteCodigo: string | null | undefined, opNumero: string | number | null | undefined): string {
  const lote = String(loteCodigo ?? '').trim() || 'não informado';
  const op = String(opNumero ?? '').trim().replace(/^OP[\s-]*/i, '').trim() || 'não informada';
  return `Lote ${lote} · OP ${op}`;
}

/** Dados do cartão que um pedido LOT_OP precisa: chaves só para a API, código e número só para a tela. */
export interface CartaoComLoteOp {
  readonly loteId: string;
  readonly opId: string;
  readonly loteCodigo: string;
  readonly opNumero: string | number;
}

/** Pedido por cartão (LOT_OP): leva o lote e a OP do cartão; a referência nunca mostra UUID. */
export function pedidoMaterialDoCartao(cartao: CartaoComLoteOp): PedidoMaterialTablet | null {
  if (!cartao.loteId || !cartao.opId) return null;
  return { loteId: cartao.loteId, opId: cartao.opId, referencia: referenciaLoteOp(cartao.loteCodigo, cartao.opNumero) };
}

/** Pedido por setor: referência legível (código · nome) e só o código da célula para a API. */
export function pedidoMaterialDoSetor(celula: CelulaColetor): PedidoMaterialTablet {
  return { celulaCodigo: celula.codigo, referencia: `Setor ${rotuloCelula(celula)}` };
}

export interface BotaoSolicitarMaterialSetorProps {
  readonly podeSolicitar: boolean;
  readonly celula: CelulaColetor | null;
  readonly rotulo?: string;
  readonly onAbrir: (pedido: PedidoMaterialTablet) => void;
}

export interface BotaoSolicitarMaterialCartaoProps {
  readonly podeSolicitar: boolean;
  readonly cartao: CartaoComLoteOp;
  readonly onAbrir: (pedido: PedidoMaterialTablet) => void;
}

/** Ação por cartão do Kanban do tablet: pede material para o lote/OP daquele cartão. */
export function BotaoSolicitarMaterialCartao({ podeSolicitar, cartao, onAbrir }: BotaoSolicitarMaterialCartaoProps): ReactElement | null {
  const pedido = podeSolicitar ? pedidoMaterialDoCartao(cartao) : null;
  if (!pedido) return null;
  return (
    <button
      type="button"
      data-acao="solicitar-material-cartao"
      aria-label={`Solicitar material para ${pedido.referencia}`}
      onClick={() => onAbrir(pedido)}
      className="min-h-[56px] rounded-xl border border-emerald-500 bg-slate-900 px-4 font-bold text-emerald-100 touch-manipulation"
    >
      Solicitar material
    </button>
  );
}

export function BotaoSolicitarMaterialSetor({ podeSolicitar, celula, rotulo = 'Solicitar material para este setor', onAbrir }: BotaoSolicitarMaterialSetorProps): ReactElement | null {
  if (!podeSolicitar || !celula) return null;
  return (
    <button
      type="button"
      data-acao="solicitar-material-setor"
      onClick={() => onAbrir(pedidoMaterialDoSetor(celula))}
      className="min-h-[56px] w-full rounded-xl border border-emerald-500 bg-slate-900 px-4 font-bold text-emerald-100 touch-manipulation"
    >
      {rotulo}
    </button>
  );
}
